import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subscription, lastValueFrom, tap, timer } from 'rxjs';
import { appendLeadingZeros } from '@app/core/helpers';
import { Constants } from '@app/constants';
import { TelephonyProvider } from '../providers/telephony/telephony.provider';
import { TelephonyStateService } from '../providers/telephony/telephony.state.service';
import { ToastService } from './toast/toast.service';
import { Logger } from '@app/core';
import { AuthenticationService } from '@app/core';
import { SignalrService } from '@app/core/signalr.service';
import { RealTimeEvents } from '@app/core/signalr.events';
import { Router } from '@angular/router';
import { CheckPermission, momentCustomInstance as moment } from '@projects/shared/src/app/utils/helper';
import { VisibilityService } from './visibility.service';
import { AppConfigurationService } from './app.configuration.service';
import { formatDate } from '../utils';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { ConnectionState, ConnectionServiceOptions, ConnectionService } from './network.service';
import { LoaderService } from '@app/core/loader.service';
import { CampaignService } from './campaign.service';

const log = new Logger('AgentStatusService');

@Injectable({
  providedIn: 'root',
})
export class AgentStatusService implements OnDestroy {
  subscriptions = new Array<Subscription>();
  currentStatus = Constants.STATUSES.LoggedIn;
  reservedStatus = Constants.STATUSES.LoggedIn;
  lastStatusBeforeCall = Constants.STATUSES.Break;
  statuses: Array<StatusDTO> = [];
  private callbackToWaitTimeoutId: any;

  duration: string = '00:00:00';

  statusTimerSubscription!: Subscription;
  statusChangeSubscription!: Subscription;
  loading: boolean = false;
  callType: string = '';
  breakSettings: any = {};
  totalBreaktime = 0;
  actualBreakTime = 0;
  totalBreakDuration = '';
  showTotalBreakTime = false;
  currentState!: ConnectionState;
  internetState!: boolean;
  lastDisconnectTime: any;
  waitStatusChange = false;
  events = [];
  isSkillUpdatedOnCall = false;
  constructor(
    public telephony: TelephonyProvider,
    public state: TelephonyStateService,
    public toast: ToastService,
    private authService: AuthenticationService,
    private signlarService: SignalrService, 
    private router: Router,
    private zone: NgZone,
    private visibiltyService: VisibilityService,
    private appConfig: AppConfigurationService,
    private http: HttpClient,
    private connectionService: ConnectionService,
    private loaderService: LoaderService,
    private campaignService: CampaignService
  ) {
    this.subscriptions.push(
      this.telephony.voice.events.subscribe(async (event) => {
        switch (event.type) {
          case 'Activity':
            switch (event.name) {
              case 'init':
                this.loading = true;
                this.startStatusChangeMonitor();
                this.endStatusTimer();
                this.actualBreakTime = this.totalBreaktime;
                break;
              case 'failed':
                this.loading = false;
                this.endStatusChangeMonitor();
                break;
              case 'update': {
                const { activity, date } = event.data;
                const status = Constants.STATUSES[activity];
                let isHoldStatus: boolean = status.FriendlyName === Constants.STATUSES.Hold.FriendlyName;
                if (status) {
                  //* Portmore New Implementaion For changing callback to wait after some seconds
                  this.callbackToWaitStatusInterval(status);
                  //Event Invoke on status change
                  this.currentStatus = status;
                  const excluded = [
                    Constants.STATUSES.Wrap.FriendlyName,
                    Constants.STATUSES.Busy.FriendlyName,
                    Constants.STATUSES.Hold.FriendlyName,
                  ];
                  if (excluded.indexOf(activity) == -1) {
                    this.statusChangeEvent(status.FriendlyName, this.reservedStatus.FriendlyName);
                    this.reservedStatus = status;
                    this.lastStatusBeforeCall = status;
                  } else {
                    if (
                      status.FriendlyName == Constants.STATUSES.Wrap.FriendlyName ||
                      status.FriendlyName == Constants.STATUSES.Hold.FriendlyName
                    ) {
                      this.statusChangeEvent(status.FriendlyName, this.reservedStatus.FriendlyName);
                      this.reservedStatus = status;
                      this.campaignService.removeNotification();
                    } else {
                      let lastStatus: any = window.localStorage.getItem('last_status');
                      lastStatus = JSON.parse(lastStatus);
                      let lastStatusSec = 0;
                      if (
                        status.FriendlyName === Constants.STATUSES.Busy.FriendlyName &&
                        lastStatus.status === Constants.STATUSES.Busy.FriendlyName
                      ) {
                        const date = moment(lastStatus.time);
                        const now = moment();
                        lastStatusSec = now.diff(date, 'seconds');
                      }

                      this.statusChangeEvent(
                        status.FriendlyName,
                        this.reservedStatus.FriendlyName,
                        lastStatusSec.toString()
                      );
                      this.reservedStatus = Constants.STATUSES.Busy;
                    }
                  }
                  let lastStatus: any = window.localStorage.getItem('last_status');
                  //check for hold status
                  if (lastStatus) {
                    lastStatus = JSON.parse(lastStatus);
                    if (lastStatus.status !== status.FriendlyName && !isHoldStatus) {
                      const d = moment(date).toISOString();
                      window.localStorage.setItem(
                        'last_status',
                        JSON.stringify({ status: status.FriendlyName, time: d })
                      );
                    }
                  } else {
                    let s = { status: status.FriendlyName, time: moment(date).toISOString() };
                    window.localStorage.setItem('last_status', JSON.stringify(s));
                  }

                  this.startStatusTimer(isHoldStatus);
                  this.loading = false;
                  this.endStatusChangeMonitor();
                  if (status.FriendlyName == Constants.STATUSES.Callback.FriendlyName) {
                    this.zone.run(() => {
                      this.router.navigate(['/agent/summaries/callback']);
                    });
                  } else if (status.FriendlyName == Constants.STATUSES.Chat.FriendlyName) {
                    if (CheckPermission('agent_chat', '', this.authService))
                      this.zone.run(() => {
                        this.router.navigate(['/agent/chats/live']);
                      });
                  }
                  if (status.FriendlyName == 'Wait') {
                    this.waitStatusChange = true;
                    setTimeout(() => {
                      this.waitStatusChange = false;
                    }, 5000);
                  }
                  else {
                    this.waitStatusChange = false;
                  }
                } else {
                  //log.error('Status not found');
                }
                break;
              };
              case 'set-loading': {
                this.loading = event.data;
                break;
              }
            }
            break;
          //* For Inbound/Outbound type circular dependency while using campaign bit
          case 'Call':
            switch (event.name) {
              case 'accept':
                const { callType } = event.data;
                this.callType = callType.replace(/\b[a-z]/g, (x: any) => x.toUpperCase());
                break;
              case 'disconnect':
                this.callType = '';
                break;
            }
            break;
          case 'Hold':
            switch (event.name) {
              case 'init':
                this.endStatusTimer();
                break;
              case 'completed':
                this.endStatusTimer();
                break;
            }
            break;
          case 'Reservation':
            switch (event.name) {
              case 'timeout':
                this.telephony.voice.sync(null, this.currentStatus, this.lastStatusBeforeCall, this.state.onCall);
                break;
            }
            break;
          case 'Worker': {
            switch (event.name) {
              case 'update': {
                const activity = event.data;
                if (activity != this.currentStatus.FriendlyName) {
                  if (activity == Constants.STATUSES.Offline.FriendlyName) {
                    if (state.onCall) {
                      this.telephony.voice.endCall(null, false);
                    }
                    else {
                      console.log(`Worker is offline on twilio and ${this.currentStatus.FriendlyName} on flecx`);
                      this.telephony.voice.changeStatus(null, this.currentStatus.FriendlyName);
                    }
                  }
                  else if (this.currentStatus.FriendlyName == Constants.STATUSES.Wait.FriendlyName || this.currentStatus.FriendlyName == Constants.STATUSES.Outbound.FriendlyName) {
                    console.log(new Date().toLocaleString() + " | Network has changed. Status changed to Twilio's Status");
                    if (state.onCall) {
                      this.telephony.voice.endCall(null, false);
                    }
                    else {
                      console.log(`Worker is offline on twilio and ${this.currentStatus.FriendlyName} on flecx`);
                      this.telephony.voice.changeStatus(null, this.currentStatus.FriendlyName);

                      // this.toast.warning("You have been put to Break status due to network error");
                      // const response = await lastValueFrom(this.addUserStatusDB(activity));
                      // this.telephony.voice.events.next({
                      //   type: 'Activity',
                      //   name: 'update',
                      //   data: { activity: activity, date: response.Id },
                      // });
                    }
                  }
                }
              }
            }
            break;
          }
        }
      })
    );
    this.subscriptions.push(
      this.visibiltyService.visibilityChange.subscribe((e: any) => {
        if (e.target.hidden) {
          this.endStatusTimer();
        } else {
          if (!this.loading) {
            this.startStatusTimer();
          }
        }
      })
    );
    this.subscriptions.push(
      this.authService.onlogout.subscribe((x: any) => {
        this.statusChangeEvent(x, this.reservedStatus.FriendlyName);
      })
    );
    this.subscriptions.push(
      this.signlarService.listen(RealTimeEvents.OnAgentStatusChange).subscribe((x: any) => {
        this.telephony.voice.events.next({ type: 'Status', name: 'status-change', data: x });
      })
    );
    this.subscriptions.push(
      this.signlarService.listen(RealTimeEvents.OnAgentUpdate).subscribe((x: any) => {
        console.log(new Date().toLocaleString() + '  ➤  OnAgentUpdate Signal R Events ==> ', x);
        this.isSkillUpdatedOnCall = true;
        if (this.state.onCall) {
          this.updateStatus('Break');
        }
        else {
          this.telephony.voice.events.next({ type: 'AgentUpdate', name: 'update', data: '' });
        }
      })
    );

    let isShowOutboundStatus = CheckPermission('agent_calls', 'agent_calls_leads', this.authService);
    if (!isShowOutboundStatus) {
      this.statuses = this.statuses.filter((obj: any) => obj.Name !== 'Outbound');
    }
    this.statuses = this.appConfig.getByIdentifier('AgentStatuses').Fields;
    //* After Portmore Extra Statuses are set in campaign collection
    //* Append campaign status with client status
    //* Remove duplicates if any
    this.mapStatusesWithCampaignExtraStatus();

    this.breakSettings = this.appConfig.getByIdentifier('BreakSettings').Fields;
    if (this.breakSettings.is_enable) {
      this.getTotalBreakTime().subscribe((x: any) => {
        this.totalBreaktime = x;
        this.actualBreakTime = x;
        this.removeBreakStatus(x, false);
      });
    }
    this.configureInternetMonitor();
    this.subscriptions.push(
      this.signlarService.reconnectEvent.subscribe(() => {
        console.log(new Date().toLocaleString() + '  ➤  SignalR reconnected, checking internet status')
        if (this.currentState.hasInternetAccess) {
          console.log(new Date().toLocaleString() + '  ➤  SignalR reconnected, internet is available, syncing data');
          this.telephony.voice.sync(null, this.currentStatus, this.lastStatusBeforeCall, this.state.onCall);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => {
      x.unsubscribe();
    });
  }

  //Status Timer Functions
  endStatusTimer() {
    if (this.statusTimerSubscription) {
      this.statusTimerSubscription.unsubscribe();
    }
    this.duration = '00:00:00';
  }
  startStatusTimer(isHold: boolean = false) {
    if (this.statusTimerSubscription) {
      this.endStatusTimer();
    }

    let hour: any = 0;
    let sec: any = 0;
    let min: any = 0;

    let lastStatus: any = window.localStorage.getItem('last_status');
    if (!lastStatus) {
      return;
    }
    let status = '';
    if (lastStatus) {
      let statusTime: any = {};
      if (isHold) {
        statusTime = moment().toISOString();
        status = Constants.STATUSES.Hold.FriendlyName;
      } else {
        statusTime = JSON.parse(lastStatus);
        status = statusTime.status;
      }

      const date = moment(statusTime.time);
      const now = moment();
      hour = now.diff(date, 'hours') % 60;
      min = now.diff(date, 'minutes') % 60;
      sec = now.diff(date, 'seconds') % 60;
      if (sec > 0) {
        sec -= 1;
      }
      if (sec >= 60) {
        min += 1;
        sec = 0;
        if (min == 60) {
          min = 0;
          sec = 0;
          hour += 1;
        }
      }
      if (this.breakSettings.is_enable && this.breakSettings.statuses.indexOf(status) != -1) {
        let time = hour * 3600 + min * 60 + sec;
        this.totalBreaktime = this.actualBreakTime + time;
      }
    }

    this.statusTimerSubscription = timer(0, 1000).subscribe((x: number) => {
      sec += 1;
      if (sec == 60) {
        min += 1;
        sec = 0;
        if (min == 60) {
          min = 0;
          sec = 0;
          hour += 1;
        }
      }
      this.duration = appendLeadingZeros(hour) + ':' + appendLeadingZeros(min) + ':' + appendLeadingZeros(sec);
      this.setBreakStatusTime(status, 1);
    });
  }

  setBreakStatusTime(status: string, seconds: number) {
    if (this.breakSettings.is_enable && this.breakSettings.statuses.indexOf(status) != -1) {
      this.totalBreaktime += seconds;
      this.totalBreakDuration = formatDate(this.totalBreaktime);
      this.showTotalBreakTime = true;
      this.removeBreakStatus(this.totalBreaktime, true);
    } else {
      this.showTotalBreakTime = false;
      this.totalBreakDuration = '';
      this.actualBreakTime = this.totalBreaktime;
    }
  }
  // Return current Status
  checkStatus(status: string) {
    return this.currentStatus.FriendlyName === status;
  }

  setStatus(status: any) {
    this.currentStatus = status;
    this.reservedStatus = status;
  }
  updateStatus(status: any) {
    if (this.state.onReservationReceived) {
      const msg = 'You have been reserved for next call. Cannot change status while on reservation.';
      log.info(msg);
      this.toast.info(msg);
      return;
    }
    if (this.waitStatusChange) {
      this.toast.info("Please wait ...");
      return;
    }

    const new_status = { ...(Constants.STATUSES as any)[status] };
    const current = this.currentStatus;
    let reserved;
    if (
      current.FriendlyName != Constants.STATUSES.Wrap.FriendlyName &&
      current.FriendlyName != Constants.STATUSES.Hold.FriendlyName &&
      current.FriendlyName != Constants.STATUSES.Busy.FriendlyName
    ) {
      reserved = { ...current };
    } else {
      reserved = { ...new_status };
    }
    log.info('Reserved status', reserved);
    if (this.state.onCall) {
      //this.reservedStatus = reserved;
      if (this.lastStatusBeforeCall.FriendlyName == reserved.FriendlyName) {
        this.toast.info(`You have already reserved ${reserved.FriendlyName} status`);
        return
      }
      this.lastStatusBeforeCall = reserved;
      this.toast.success('Your status has been reserved');
      this.telephony.voice.reserveStatus(null, reserved);
      return;
    }

    this.telephony.voice.changeStatus(null, status);
  }
  endStatusChangeMonitor() {
    if (this.statusChangeSubscription) {
      this.statusChangeSubscription.unsubscribe();
    }
  }
  startStatusChangeMonitor() {
    this.endStatusChangeMonitor();
    let interval = 0;
    this.statusChangeSubscription = timer(0, 1000).subscribe((x: number) => {
      interval++;
      if (interval >= 45) {
        if (this.loading) {
          if (this.loading) {
            this.loading = false;
            this.toast.error('Cannot change status due to network glitches. Please try again later.');
            this.endStatusChangeMonitor();
          }
        }
      }
    });
  }
  async statusChangeEvent(selectedStatus: string, reservedStatus: string, statusTime: string = '0') {
    if (!this.authService.authData) return;
    let clientId = this.authService.authData.ClientId;
    const SupervisorCampaignCodes = this.authService.authData.CampaignInfo.Supervisor ? this.getCompaignCode(this.authService.authData.CampaignInfo.Supervisor.Campaigns).join(", ") : "";
    const AgentCampaignCodes = this.authService.authData.CampaignInfo.Agent ? this.getCompaignCode(this.authService.authData.CampaignInfo.Agent.Campaigns).join(", ") : "";
    await this.signlarService.invoke(RealTimeEvents.SendAgentStatusChangeNotification, {
      UserId: this.authService.getUserId(),
      ClientId: clientId,
      Campaigns: this.authService.getAllCampaignCode(),
      Email: this.authService.authData.Email, 
      NewStatus: selectedStatus,
      OldStatus: reservedStatus,
      Inbound: this.callType === 'Inbound' ? 1 : 0,
      Outbound: this.callType === 'Outbound' ? 1 : 0,
      StatusTime: statusTime,
      LoginTime: this.authService.loginDuration,
      Role: this.authService.authData.Role,
      AssignedApps: this.authService.authData.AssignedApp,
      SupervisorCampaignCodes: SupervisorCampaignCodes,
      AgentCampaignCodes: AgentCampaignCodes,
    });


  }
private getCompaignCode(data: Array<any>): Array<string> {
    const codes: Array<string> = [];
    data.forEach(item => {
      codes.push(item.Code);
    })
    return codes

  }
  //* Portmore New Work For Randomly Change Status to Wait from Callback
  callbackToWaitStatusInterval(status: any) {
    let primaryCampaign = this.authService.getDefaultCampaign()!;
    if (
      primaryCampaign?.Settings?.AutoShiftStatus?.IsImplemented &&
      status.FriendlyName === primaryCampaign.Settings.AutoShiftStatus.From
    ) {
      this.executeAfterRandomDelay(
        primaryCampaign.Settings.AutoShiftStatus.IntervalStart,
        primaryCampaign.Settings.AutoShiftStatus.IntervalEnd,
        () => {
          //Change Status to To
          this.telephony.voice.changeStatus(null, primaryCampaign.Settings.AutoShiftStatus.To);
          //route agent to dashboard after status change
          this.router.navigate(['/agent/dashboard']);
        }
      );
    } else {
      if (this.callbackToWaitTimeoutId) {
        this.cancelExecuteAfterRandomDelay();
      }
    }
  }

  executeAfterRandomDelay(start: number = 50, end: number = 90, callback: () => void): void {
    const getRandomDelay = () => Math.random() * (end - start) * 1000 + start * 1000;
    const delay = getRandomDelay();
    this.callbackToWaitTimeoutId = setTimeout(callback, delay);
  }

  cancelExecuteAfterRandomDelay(): void {
    clearTimeout(this.callbackToWaitTimeoutId);
    this.callbackToWaitTimeoutId = null;
  }

  mapStatusesWithCampaignExtraStatus() {
    let campaign: CampaignDTO | null = this.authService.getDefaultCampaign();
    if (campaign) {
      let extraStatuses: Array<any> = campaign.Settings?.ExtraStatus ?? [];
      extraStatuses.forEach((extra) => {
        // Check if the status with the same Key exists in the status array
        const statusIndex = this.statuses.findIndex((s) => s.Key === extra.Key);

        if (extra.IsEnabled) {
          // If IsEnabled is true and the status doesn't already exist, add it
          if (statusIndex === -1) {
            // Remove IsEnabled from the object before adding to status
            const { IsEnabled, ...statusToAdd } = extra;
            this.statuses.push(statusToAdd);
          }
        } else {
          // If IsEnabled is false and the status exists, remove it
          if (statusIndex !== -1) {
            this.statuses.splice(statusIndex, 1);
          }
        }
      });
    }

    // console.log(new Date().toLocaleString() + '  ➤  this.statuses ==> ', this.statuses);
  }

  getTotalBreakTime() {
    let url = `${Constants.APIS.USER.TOTAL_BREAK_TIME}?Statuses=${this.breakSettings.statuses.join(
      ','
    )}&StartDate=${moment().startOf('day').toISOString()}&EndDate=${moment().endOf('day').toISOString()}`;
    return this.http.disableLoader().get<any>(url);
  }

  removeBreakStatus(seconds: number, changeStatus: boolean) {
    const minutes = Math.floor(seconds / 60);
    const interval = this.breakSettings.interval + this.breakSettings.extra_interval;
    if (minutes >= interval) {
      this.showTotalBreakTime = false;
      this.statuses = this.statuses.filter((x) => this.breakSettings.statuses.indexOf(x.FriendlyName) == -1);
      this.authService.setByIdentifier('AgentStatuses', this.statuses);
      this.toast.info('Your total break time exceeded. Break status not available for rest of the day.');
      if (this.breakSettings.statuses.includes(this.currentStatus.FriendlyName) && changeStatus) {
        this.telephony.voice.changeStatus(null, Constants.STATUSES.Wait.FriendlyName);
      }
      this.breakSettings.is_enable = false;
    }
  }
  addUserStatusDB(status: any) {
    const user = this.authService.authData;
    let data = {
      Status: status,
      UserId: user.Id,
      Version: '1.0.0',
      Campaigns: user.Campaigns?.map((x: any) => ({ Id: x.Id, Name: x.Name, Code: x.Code })),
      CallSid: "",
    };
    return this.http.disableLoader().post<any>(Constants.APIS.USER.STATUS_LOG, data);
  }
  configureInternetMonitor() {
    const options: ConnectionServiceOptions = {
      enableHeartbeat: true,
      heartbeatUrl: environment.ping.url || `${environment.api.url}${environment.api.server}/log/status`,
      heartbeatInterval: environment.ping.timeout,
    };
    this.subscriptions.push(
      this.connectionService
        .monitor(options)
        .pipe(
          tap((newState: ConnectionState) => {
            this.currentState = newState;
            if (this.currentState.hasNetworkConnection && this.currentState.hasInternetAccess) {
              if (this.currentState.hasInternetAccess != this.internetState) {
                if (this.lastDisconnectTime) {
                  console.log(new Date().toLocaleString() + '  ➤  Internet Restored');

                  const sec = moment().diff(this.lastDisconnectTime, 'seconds');
                  console.log(new Date().toLocaleString() + '  ➤  Time difference to re-connectivity is :: ' + sec);
                  console.log(new Date().toLocaleString() + '  ➤  Sync will run after 3 seconds');
                  const provider = this.authService.getTelephonyProvider();
                  const TIME = provider == 'ibex' ? 1 : 30;
                  const timeout = provider == 'ibex' ? 3000 : 1000;
                  if (sec >= TIME) {
                    setTimeout(() => {
                      this.telephony.voice.sync(
                        null,
                        this.currentStatus,
                        this.lastStatusBeforeCall,
                        this.state.onCall
                      );
                    }, timeout);
                  }
                  this.loaderService.toggleMainLoader(false);
                  Logger.pushLogs();
                }
              }
              this.internetState = true;
              this.lastDisconnectTime = null;
              Logger.setInternetState(true);
            } else {
              this.internetState = false;
              Logger.setInternetState(false);
              if (!this.lastDisconnectTime) {
                this.lastDisconnectTime = new Date();
              }
            }
          })
        )
        .subscribe()
    );
  }
}

interface StatusDTO {
  Name: string;
  FriendlyName: string;
  Key: string;
  Icon: string;
  Permission: string;
}

interface CampaignDTO {
  Code: string;
  Id: string;
  Name: string;
  Settings: {
    Note: boolean;
    ExtraStatus: Array<{
      Name: string;
      FriendlyName: string;
      Key: string;
      Icon: string;
      IsEnabled: boolean;
    }>;
    AutoShiftStatus: {
      From: string;
      To: string;
      IntervalStart: number;
      IntervalEnd: number;
      IsImplemented: boolean;
    };
  };
}
